import { Spinner } from '@hvac/ui';
import type { QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useAuth } from '../shared/auth/auth-context.js';
import { createAppRouter, type AppRouter } from './router.js';

/**
 * Wires the live authentication context into the router.
 *
 * The router is created once and re-rendered with fresh context whenever auth
 * changes, which is what lets `beforeLoad` guards react to a sign-out in
 * another tab.
 *
 * Rendering is held back until the stored session has been read. Route guards
 * would otherwise run against `status: 'loading'` and bounce a signed-in user
 * to the login screen for a frame.
 */
export function App({ queryClient }: { queryClient: QueryClient }) {
  const auth = useAuth();
  const router = useMemo<AppRouter>(() => createAppRouter(queryClient), [queryClient]);

  if (auth.status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="size-8 text-brand-600" label="Restoring your session" />
      </div>
    );
  }

  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}
